/**
 * The shapes SaveScreen takes and reports: the extraction fields it shows, the
 * governed routing detector's result and the human's routing choice, the
 * decision mode, the T-10 context answers, and the fixed option lists for the
 * judgment fields. Shared by SaveScreen and the shell.
 */

export interface ExtractionField {
  value: string;
  source_refs: string[];
}

export interface SaveScreenExtraction {
  room?: ExtractionField;
  description?: ExtractionField;
  item_text?: ExtractionField;
}

export type DecisionMode = "raise" | "resolve";

/** A result from the existing governed review detector. This component only
 * consumes the result: it never classifies text or turns detection into commit
 * authority. `reviewedText` binds the result to the exact text that was checked. */
export interface RoutingDetection {
  reviewedText: string;
  kinds: Array<"action" | "decision" | "material_change">;
}

export type ExplicitRoutingChoice = "action" | "decision" | "log_only";

/** The judgment-field vocabulary this build offers (T-12) — a fixed,
 * human-facing tier list; NOT derived from any extraction/AI source. */
export const CRITICALITY_TIERS = ["low", "medium", "high"] as const;

/** M-TAG1 (BUI-92): the canonical PHASE vocabulary — the Excel-sourced master
 * decision list's controlled 10-phase set (notes/product-notes/
 * master_decision_list.md: "The ten phases (numbered 0-9)"). This is the SAME
 * fixed set the mobile surface tags against, so desktop matches rather than
 * inventing its own — the whole point of the parity ticket. A decision's phase
 * is the phase of its earliest gate. The stored value is the numeric id
 * (`"0".."9"`), stable for the downstream master-decision-list coverage view to
 * group by; the label is display-only. NOT derived from extraction/AI
 * (AC-1g-06), exactly like the criticality tiers above. */
export const DECISION_MAKER_OPTIONS = [
  "Client", "Architect", "Structural", "Civil", "GC", "Geotech", "Surveyor",
  "Subcontractor/trade", "Consultant", "Utility/jurisdiction",
] as const;

/** T-10's context-block answers — riding the queued Event via
 * `payload.anchor_text`/room context fields (§8.1); this component only
 * ever mirrors them out, never constructs the Event itself. */
export interface SaveScreenContext {
  room: string;
  intent: string;
}

export const EXTRACTION_KEYS = ["room", "description", "item_text"] as const;
